import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../utils/apiHelper';

import SubscriptionDetailsContent from './SubscriptionDetailsModal';
import CreateFreeSubscriptionModal from './CreateFreeSubscriptionModal';
import { useAuth } from '../../context/AuthContext';
import { formatPrice } from '../../utils/formatHelper';
import { useModal } from '../hooks/useModal';
import { useDataTable } from '../hooks/useDataTable';
import PageHeader from '../components/shared/PageHeader';
import DataTable from '../components/shared/DataTable';
import FilterBar from '../components/shared/FilterBar';
import StatusBadge from '../components/shared/StatusBadge';
import ConfirmModal from '../components/shared/ConfirmModal';
import ActionDrawer from '../components/shared/ActionDrawer';
import LoadingSpinner from '../../components/LoadingSpinner';
import { showSuccess, showError, showInfo, showServerError, showWarning } from '../../utils/toastHelper';

function SubscriptionManagement() {
  const navigate = useNavigate();
  // Modal states
  const {
    isOpen: isDetailsOpen,
    open: openDetails,
    close: closeDetails
  } = useModal();

  const {
    isOpen: isFreeSubscriptionOpen,
    open: openFreeSubscription,
    close: closeFreeSubscription
  } = useModal();

  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Address editing drawer states
  const {
    isOpen: isAddressOpen,
    open: openAddress,
    close: closeAddress
  } = useModal();
  const [editingAddressSubscription, setEditingAddressSubscription] = useState(null);
  const [addressFormData, setAddressFormData] = useState({
    shippingAddress: {
      fullName: '',
      address: '',
      city: '',
      district: '',
      country: 'Turkey',
      phone: '',
      zipCode: ''
    },
    billingAddress: {
      fullName: '',
      address: '',
      city: '',
      district: '',
      country: 'Turkey',
      phone: '',
      zipCode: '',
      type: 'individual'
    }
  });
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [addressLoading, setAddressLoading] = useState(false);

  // Payment status check states
  const [paymentCheckLoading, setPaymentCheckLoading] = useState({});
  const [paymentCheckResults, setPaymentCheckResults] = useState({}); // Kept for consistency though not directly used in render except via toast? Or maybe used in modal?

  // Cancel subscription confirmation modal states
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showCancelSuccess, setShowCancelSuccess] = useState(false);
  const [subscriptionToCancel, setSubscriptionToCancel] = useState(null);
  const [cancelResponse, setCancelResponse] = useState(null);
  const [deleteSubscriptionId, setDeleteSubscriptionId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Data Table Hook
  const {
    data: subscriptions,
    loading,
    pagination,
    filters,
    setFilters,
    handlePageChange,
    handleLimitChange,
    refresh
  } = useDataTable('/subscriptions', {
    initialFilters: {
      status: 'all',
      search: '',
      startDate: '',
      endDate: ''
    }
  });

  useEffect(() => {
    // Responsive davranış için pencere boyutu değişikliklerini izle
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter Configuration
  const filterConfig = [
    {
      type: 'select',
      key: 'status',
      label: 'Durum',
      options: [
        { value: 'all', label: 'Tüm Durumlar' },
        { value: 'active', label: 'Aktif' },
        { value: 'waiting', label: 'Duraklatılmış' },
        { value: 'cancelled', label: 'İptal Edilmiş' },
        { value: 'pending', label: 'Beklemede' },
        { value: 'failed', label: 'Hata Durumu' }
      ],
      defaultValue: 'all'
    },
    {
      type: 'dateRange',
      key: 'date',
      startLabel: 'Başlangıç Tarihi',
      endLabel: 'Bitiş Tarihi',
      startPlaceholder: 'Başlangıç',
      endPlaceholder: 'Bitiş'
    }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'all',
      search: '',
      startDate: '',
      endDate: ''
    });
  };

  const handleViewDetails = (subscription) => {
    setSelectedSubscription(subscription);
    openDetails();
  };

  const getStatusForBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'ACTIVE';
      case 'waiting': return 'PAUSED';
      case 'cancelled': return 'CANCELLED_SUB';
      case 'pending': return 'PENDING';
      case 'failed': return 'CANCELLED_SUB';
      default: return status;
    }
  };

  const handleUpdateAllPrices = async () => {
    try {
      const response = await apiRequest('/admin/subscriptions/update-all-prices', {
        method: 'POST'
      });

      if (response.success) {
        showSuccess('Abonelik fiyatları güncelleme işlemi başlatıldı');
        refresh();
      } else {
        throw new Error(response.message || 'Hata oluştu');
      }
    } catch (error) {
      console.error('Update prices error:', error);
      showError(error.message || 'Fiyat güncellenirken hata oluştu');
    }
  };

  const handleEditSubscription = (subscription) => {
    const isFreeSubscription = subscription.paymentStatus === 'FREE' ||
      subscription.metadata?.isFree === true ||
      subscription.amount === 0 ||
      subscription.plan?.price === 0 ||
      (subscription.amount && parseFloat(subscription.amount) < 1);

    const isActiveOrPending = subscription.status === 'active' || subscription.status === 'pending';

    if (isFreeSubscription && isActiveOrPending) {
      // Subscription verisini modal'ın beklediği formata dönüştür
      const formattedSubscription = {
        id: subscription.id,
        userId: subscription.customer?.id || subscription.userId,
        interval: subscription.interval || subscription.plan?.interval || 'MONTHLY',
        products: subscription.products || [],
        shippingAddress: subscription.customerInfo?.shippingAddress || subscription.shippingAddress || {},
        billingAddress: subscription.customerInfo?.billingAddress || subscription.billingAddress || {},
        startDate: subscription.start_date || subscription.startDate,
        notes: subscription.notes || '',
        user: {
          ...subscription.customer,
          fullName: subscription.customer?.name || subscription.customer?.fullName || ''
        }
      };

      setEditingSubscription(formattedSubscription);
      openFreeSubscription();
    } else if (!isFreeSubscription) {
      showInfo('Sadece ücretsiz abonelikler düzenlenebilir.');
    } else {
      showInfo('Sadece aktif veya bekleyen abonelikler düzenlenebilir.');
    }
  };

  const handleSubscriptionSuccess = () => {
    refresh();
    setEditingSubscription(null);
  };

  const confirmCancelSubscription = async () => {
    if (!subscriptionToCancel) return;

    try {
      setActionLoading(true);
      const response = await apiRequest(`/subscriptions/${subscriptionToCancel.id}/cancel`, {
        method: 'PUT'
      });

      if (response.success) {
        showSuccess('Abonelik başarıyla iptal edildi');
        setShowCancelConfirm(false);
        setSubscriptionToCancel(null);

        // Eğer detaylar varsa success modal göster
        if (response.details) {
          setCancelResponse(response.details);
          setShowCancelSuccess(true);
        } else {
          refresh();
        }
      } else {
        throw new Error(response.message || 'Abonelik iptal edilirken bir hata oluştu');
      }
    } catch (error) {
      console.log('Error:', error);
      setShowCancelConfirm(false);
      setSubscriptionToCancel(null);
      if (error.apiError) {
        showServerError();
      } else {
        showError(error.message || 'Abonelik iptal edilirken bir hata oluştu');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const confirmDeleteSubscription = async () => {
    if (!deleteSubscriptionId) return;

    try {
      setActionLoading(true);
      const response = await apiRequest(`/subscriptions/${deleteSubscriptionId.id}`, {
        method: 'DELETE'
      });

      if (response.success) {
        showSuccess('Abonelik başarıyla silindi');
        setShowDeleteConfirm(false);
        setDeleteSubscriptionId(null);
        refresh();
      } else {
        throw new Error(response.message || 'Abonelik silinirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Failed to delete subscription:', error);
      setShowDeleteConfirm(false);
      setDeleteSubscriptionId(null);
      if (error.isAuthError) return;
      if (error.apiError) {
        showServerError();
      } else {
        showError(error.message || 'Abonelik silinirken bir hata oluştu');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handlePaymentStatusCheck = async (subscription, checkType = 'subscription') => {
    const checkId = `${subscription.id}-${checkType}`;
    setPaymentCheckLoading(prev => ({ ...prev, [checkId]: true }));

    try {
      let queryParams = '';
      if (checkType === 'subscription') {
        queryParams = `?subscriptionId=${subscription.id}`;
      } else if (checkType === 'user') {
        queryParams = `?userId=${subscription.customer.id}`;
      }

      const response = await apiRequest(`/subscriptions/payment-status${queryParams}`);

      if (response.success) {
        setPaymentCheckResults(prev => ({
          ...prev,
          [checkId]: response
        }));

        if (response.paymentCheck?.needsAction) {
          showWarning(`${subscription.customer.name}: ${response.paymentCheck.message}`);
        } else {
          showSuccess(`${subscription.customer.name}: Ödeme durumu kontrol edildi`);
        }
      } else {
        showError(`${subscription.customer.name}: ${response.message || 'Ödeme durumu kontrol edilemedi'}`);
      }
    } catch (error) {
      console.error('Payment status check error:', error);
      showError(error.message || 'Ödeme durumu kontrol edilirken bir hata oluştu');
    } finally {
      setPaymentCheckLoading(prev => ({ ...prev, [checkId]: false }));
    }
  };

  // Address editing functions
  const handleEditAddress = (subscription) => {
    setEditingAddressSubscription(subscription);
    const customerInfo = subscription.customerInfo || {};
    const existingShipping = customerInfo.shippingAddress || {};
    const existingBilling = customerInfo.billingAddress || {};

    const getFullName = (addressObj, customerInfo) => {
      if (addressObj?.contactName) return addressObj.contactName;
      if (addressObj?.fullName) return addressObj.fullName;
      if (customerInfo?.name && customerInfo?.surname) {
        return `${customerInfo.name} ${customerInfo.surname}`.trim();
      }
      return '';
    };

    const getPhone = (addressObj) => {
      if (addressObj?.gsmNumber) return addressObj.gsmNumber;
      if (addressObj?.phone) return addressObj.phone;
      return '';
    };

    const formData = {
      shippingAddress: {
        fullName: getFullName(existingShipping, customerInfo),
        address: existingShipping?.address || '',
        city: existingShipping?.city || '',
        district: existingShipping?.district || '',
        country: existingShipping?.country || 'Turkey',
        phone: getPhone(existingShipping),
        zipCode: existingShipping?.zipCode || ''
      },
      billingAddress: {
        fullName: getFullName(existingBilling, customerInfo),
        address: existingBilling?.address || '',
        city: existingBilling?.city || '',
        district: existingBilling?.district || '',
        country: existingBilling?.country || 'Turkey',
        phone: getPhone(existingBilling),
        zipCode: existingBilling?.zipCode || '',
        type: existingBilling?.type || 'individual'
      }
    };

    // Determine if addresses are same (simple comparison)
    // Note: original code checked fields individually which is safer
    const areSame = (
      formData.shippingAddress.fullName === formData.billingAddress.fullName &&
      formData.shippingAddress.address === formData.billingAddress.address &&
      formData.shippingAddress.city === formData.billingAddress.city &&
      formData.shippingAddress.district === formData.billingAddress.district &&
      formData.shippingAddress.country === formData.billingAddress.country &&
      formData.shippingAddress.phone === formData.billingAddress.phone &&
      formData.shippingAddress.zipCode === formData.billingAddress.zipCode
    );

    setAddressFormData(formData);
    setSameAsBilling(areSame);
    setValidationErrors({});
    openAddress();
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();

    // Address Validation (Simplified for brevity but covers essentials)
    const errors = {};
    if (!addressFormData.shippingAddress.fullName.trim()) errors['shippingAddress.fullName'] = 'Gerekli';
    if (!addressFormData.shippingAddress.address.trim()) errors['shippingAddress.address'] = 'Gerekli';
    if (!addressFormData.shippingAddress.city.trim()) errors['shippingAddress.city'] = 'Gerekli';
    if (!addressFormData.shippingAddress.district.trim()) errors['shippingAddress.district'] = 'Gerekli';
    if (!addressFormData.shippingAddress.phone.trim()) errors['shippingAddress.phone'] = 'Gerekli';

    if (!sameAsBilling) {
      if (!addressFormData.billingAddress.fullName.trim()) errors['billingAddress.fullName'] = 'Gerekli';
      if (!addressFormData.billingAddress.address.trim()) errors['billingAddress.address'] = 'Gerekli';
      if (!addressFormData.billingAddress.city.trim()) errors['billingAddress.city'] = 'Gerekli';
      if (!addressFormData.billingAddress.district.trim()) errors['billingAddress.district'] = 'Gerekli';
      if (!addressFormData.billingAddress.phone.trim()) errors['billingAddress.phone'] = 'Gerekli';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      showError('Lütfen tüm gerekli alanları doldurun');
      return;
    }

    try {
      setAddressLoading(true);
      const requestData = {
        shippingAddress: addressFormData.shippingAddress,
        billingAddress: sameAsBilling ? {
          ...addressFormData.shippingAddress,
          type: addressFormData.billingAddress.type
        } : addressFormData.billingAddress
      };

      const response = await apiRequest(`/admin/subscriptions/${editingAddressSubscription.id}/customer-info`, {
        method: 'PUT',
        data: requestData
      });

      if (response.success) {
        showSuccess('Adres güncellendi');
        closeAddress();
        refresh();
      } else {
        if (response.errors) setValidationErrors(response.errors);
        throw new Error(response.message || 'Hata oluştu');
      }
    } catch (err) {
      console.error(err);
      if (err.isAuthError) return;
      if (err.apiError) showServerError();
      else showError(err.message || 'Güncelleme hatası');
    } finally {
      setAddressLoading(false);
    }
  };

  const handleAddressFormChange = (type, field, value) => {
    setAddressFormData(prev => {
      const newState = { ...prev, [type]: { ...prev[type], [field]: value } };
      // Auto update billing if same
      if (type === 'shippingAddress' && sameAsBilling) {
        newState.billingAddress = {
          ...newState.shippingAddress,
          [field]: value,
          type: prev.billingAddress.type
        };
      }
      return newState;
    });
    // Clear error
    if (validationErrors[`${type}.${field}`]) {
      setValidationErrors(prev => ({ ...prev, [`${type}.${field}`]: '' }));
    }
  };

  // Table Columns
  const columns = [
    {
      header: 'Müşteri',
      render: (row) => (
        <div>
          <div className="fw-bold text-dark">{row.customer?.name} {row.customer?.surname}</div>
          <div className="text-muted small">{row.customer?.email}</div>
          {row.customer?.gsmNumber && <div className="text-muted small">{row.customer.gsmNumber}</div>}
        </div>
      )
    },
    {
      header: 'Plan',
      render: (row) => (
        <div>
          <div className="fw-medium">{row.plan?.name}</div>
          <div className="text-muted small">{row.plan?.interval === 'MONTHLY' ? 'Aylık' : '2 Haftalık'}</div>
        </div>
      )
    },
    {
      header: 'Tutar',
      render: (row) => (
        <div>
          <div className="fw-bold text-dark">{formatPrice(row.plan?.price)}</div>
          <small className="text-muted">{row.products?.length} ürün</small>
        </div>
      )
    },
    {
      header: 'Durum',
      render: (row) => <StatusBadge status={getStatusForBadge(row.status)} />
    },
    {
      header: 'Son Teslimat',
      render: (row) => row.lastPaymentDate ? (
        <div>
          <div>{new Date(row.lastPaymentDate).toLocaleDateString('tr-TR')}</div>
          <small className="text-muted">Başarılı</small>
        </div>
      ) : <span className="text-muted">-</span>
    },
    {
      header: 'Sonraki Teslimat',
      render: (row) => row.nextPaymentDate ? (
        <div>
          <div>{new Date(row.nextPaymentDate).toLocaleDateString('tr-TR')}</div>
          <small className="text-muted">Planlanmış</small>
        </div>
      ) : <span className="text-muted">-</span>
    },
    {
      header: 'İşlemler',
      width: '100px',
      render: (row) => (
        <div className="dropdown" onClick={e => e.stopPropagation()}>
          <button
            className="btn btn-sm btn-light text-muted"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="fas fa-ellipsis-v"></i>
          </button>
          <ul className="dropdown-menu dropdown-menu-end border-0 shadow">
            <li>
              <button className="dropdown-item" onClick={() => handleViewDetails(row)}>
                <i className="fas fa-eye fa-fw me-2"></i> Detay
              </button>
            </li>
            <li>
              <button className="dropdown-item" onClick={() => navigate(`/admin/orders?subscriptionId=${row.id}`)}>
                <i className="fas fa-shopping-bag fa-fw me-2"></i> Siparişlerini Gör
              </button>
            </li>
            <li>
              <button className="dropdown-item" onClick={() => handleEditAddress(row)}>
                <i className="fas fa-map-marker-alt fa-fw me-2"></i> Adres Düzenle
              </button>
            </li>
            <li><hr className="dropdown-divider" /></li>
            <li>
              <button
                className="dropdown-item"
                onClick={() => handlePaymentStatusCheck(row, 'subscription')}
                disabled={paymentCheckLoading[`${row.id}-subscription`]}
              >
                {paymentCheckLoading[`${row.id}-subscription`] ? (
                  <><span className="spinner-border spinner-border-sm me-2" /> Kontrol...</>
                ) : (
                  <><i className="fas fa-search fa-fw me-2"></i> Ödeme Kontrol</>
                )}
              </button>
            </li>

            {/* Free Subscription Edit */}
            {(
              (row.paymentStatus === 'FREE' || row.metadata?.isFree === true || row.amount === 0 || row.plan?.price === 0 || (row.amount && parseFloat(row.amount) < 1)) &&
              (row.status === 'active' || row.status === 'pending')
            ) && (
                <li>
                  <button className="dropdown-item" onClick={() => handleEditSubscription(row)}>
                    <i className="fas fa-edit fa-fw me-2"></i> Abonelik Düzenle
                  </button>
                </li>
              )}

            {/* Cancel */}
            {(['active', 'pending', 'failed', 'waiting'].includes(row.status)) && (
              <>
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <button className="dropdown-item text-danger" onClick={() => {
                    setSubscriptionToCancel(row);
                    setShowCancelConfirm(true);
                  }}>
                    <i className="fas fa-ban fa-fw me-2"></i> İptal Et
                  </button>
                </li>
              </>
            )}

            {/* Delete */}
            {row.status === 'cancelled' && (
              <>
                <li><hr className="dropdown-divider" /></li>
                <li>
                  <button className="dropdown-item text-danger" onClick={() => {
                    setDeleteSubscriptionId(row);
                    setShowDeleteConfirm(true);
                  }}>
                    <i className="fas fa-trash fa-fw me-2"></i> Sil
                  </button>
                </li>
              </>
            )}
          </ul>
        </div >
      )
    }
  ];

  // Show full page loader on initial load
  if (loading && (!subscriptions || subscriptions.length === 0)) {
    return (
      <div className="container-fluid p-0">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <LoadingSpinner message="Abonelikler yükleniyor..." size="medium" />
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0">
      <PageHeader
        title="Abonelik Yönetimi"
        description="Tüm abonelikleri buradan yönetebilirsiniz"
        breadcrumbs={[{ label: 'Dashboard', path: '/admin' }, { label: 'Abonelikler' }]}
        actions={
          <div className="d-flex flex-column flex-md-row gap-2 w-100 w-md-auto">
            <button
              className="btn btn-primary btn-sm-mobile"
              onClick={() => openFreeSubscription()}
            >
              <i className="fas fa-plus me-2"></i>
              {isMobile ? 'Ücretsiz Abonelik' : 'Ücretsiz Abonelik'}
            </button>
            <button
              className="btn btn-outline-primary btn-sm-mobile"
              onClick={handleUpdateAllPrices}
            >
              <i className="fas fa-sync-alt me-2"></i>
              {isMobile ? 'Tüm Fiyatları Güncelle' : 'Tüm Fiyatları Güncelle'}
            </button>
          </div>
        }
      />

      <FilterBar
        filters={filterConfig}
        activeFilters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        searchPlaceholder="Müşteri adı veya e-posta..."
      />

      <DataTable
        tableId="subscriptions"
        columns={columns}
        data={subscriptions}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onRowClick={handleViewDetails}
        emptyState={{
          title: 'Abonelik Bulunamadı',
          message: 'Arama kriterlerinize uygun abonelik kaydı yok veya henüz hiç abonelik oluşturulmamış.',
          icon: 'calendar-xmark'
        }}
      />

      {/* Drawers */}
      <ActionDrawer
        isOpen={isDetailsOpen}
        onClose={closeDetails}
        title="Abonelik Detayları"
        width="800px"
      >
        {selectedSubscription && (
          <SubscriptionDetailsContent
            subscription={selectedSubscription}
            onClose={closeDetails}
          />
        )}
      </ActionDrawer>

      <CreateFreeSubscriptionModal
        isOpen={isFreeSubscriptionOpen}
        onClose={closeFreeSubscription}
        onSuccess={handleSubscriptionSuccess}
        editSubscription={editingSubscription}
      />

      <ActionDrawer
        isOpen={isAddressOpen}
        onClose={closeAddress}
        title="Adres Düzenle"
        width="700px"
      >
        {editingAddressSubscription && (
          <form onSubmit={handleAddressSubmit}>
            <div className="row g-3">
              <div className="col-12">
                <h6 className="border-bottom pb-2">Teslimat Adresi</h6>
              </div>
              <div className="col-md-6">
                <label className="form-label">Ad Soyad</label>
                <input
                  type="text"
                  className="form-control"
                  value={addressFormData.shippingAddress.fullName}
                  onChange={(e) => handleAddressFormChange('shippingAddress', 'fullName', e.target.value)}
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Telefon</label>
                <input
                  type="text"
                  className="form-control"
                  value={addressFormData.shippingAddress.phone}
                  onChange={(e) => handleAddressFormChange('shippingAddress', 'phone', e.target.value)}
                  required
                />
              </div>
              <div className="col-12">
                <label className="form-label">Adres</label>
                <textarea
                  className="form-control"
                  value={addressFormData.shippingAddress.address}
                  onChange={(e) => handleAddressFormChange('shippingAddress', 'address', e.target.value)}
                  required
                ></textarea>
              </div>
              <div className="col-md-4">
                <label className="form-label">Şehir</label>
                <input
                  type="text"
                  className="form-control"
                  value={addressFormData.shippingAddress.city}
                  onChange={(e) => handleAddressFormChange('shippingAddress', 'city', e.target.value)}
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">İlçe</label>
                <input
                  type="text"
                  className="form-control"
                  value={addressFormData.shippingAddress.district}
                  onChange={(e) => handleAddressFormChange('shippingAddress', 'district', e.target.value)}
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Posta Kodu</label>
                <input
                  type="text"
                  className="form-control"
                  value={addressFormData.shippingAddress.zipCode}
                  onChange={(e) => handleAddressFormChange('shippingAddress', 'zipCode', e.target.value)}
                />
              </div>
            </div>

            <div className="form-check my-3 p-3 bg-light rounded">
              <input
                className="form-check-input"
                type="checkbox"
                checked={sameAsBilling}
                id="sameAsBillingCheck"
                onChange={(e) => setSameAsBilling(e.target.checked)}
              />
              <label className="form-check-label user-select-none" htmlFor="sameAsBillingCheck">
                Fatura adresi teslimat adresiyle aynı
              </label>
            </div>

            {!sameAsBilling && (
              <div className="row g-3">
                <div className="col-12">
                  <h6 className="border-bottom pb-2">Fatura Adresi</h6>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Ad Soyad / Ünvan</label>
                  <input
                    type="text"
                    className="form-control"
                    value={addressFormData.billingAddress.fullName}
                    onChange={(e) => handleAddressFormChange('billingAddress', 'fullName', e.target.value)}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Telefon</label>
                  <input
                    type="text"
                    className="form-control"
                    value={addressFormData.billingAddress.phone}
                    onChange={(e) => handleAddressFormChange('billingAddress', 'phone', e.target.value)}
                    required
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Adres</label>
                  <textarea
                    className="form-control"
                    value={addressFormData.billingAddress.address}
                    onChange={(e) => handleAddressFormChange('billingAddress', 'address', e.target.value)}
                    required
                  ></textarea>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Şehir</label>
                  <input
                    type="text"
                    className="form-control"
                    value={addressFormData.billingAddress.city}
                    onChange={(e) => handleAddressFormChange('billingAddress', 'city', e.target.value)}
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">İlçe</label>
                  <input
                    type="text"
                    className="form-control"
                    value={addressFormData.billingAddress.district}
                    onChange={(e) => handleAddressFormChange('billingAddress', 'district', e.target.value)}
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Posta Kodu</label>
                  <input
                    type="text"
                    className="form-control"
                    value={addressFormData.billingAddress.zipCode}
                    onChange={(e) => handleAddressFormChange('billingAddress', 'zipCode', e.target.value)}
                  />
                </div>
              </div>
            )}

            {Object.keys(validationErrors).length > 0 && (
              <div className="alert alert-danger mt-3">
                <ul className="mb-0 ps-3">
                  {Object.values(validationErrors).map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
              <button type="button" className="btn btn-secondary" onClick={closeAddress}>İptal</button>
              <button type="submit" className="btn btn-primary" disabled={addressLoading}>
                {addressLoading ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </form>
        )}
      </ActionDrawer>

      {/* Confirmation Modals */}
      <ConfirmModal
        show={showCancelConfirm}
        onClose={() => {
          setShowCancelConfirm(false);
          setSubscriptionToCancel(null);
        }}
        onConfirm={confirmCancelSubscription}
        title="Aboneliği İptal Et"
        message={
          subscriptionToCancel?.cancellationRequest?.cancelOrders
            ? 'Müşteri iade talebinde bulundu lütfen inceleyiniz.\n\nBu aboneliği iptal etmek istediğinize emin misiniz?'
            : 'Bu aboneliği iptal etmek istediğinize emin misiniz?'
        }
        confirmText="İptal Et"
        cancelText="Vazgeç"
        variant="danger"
        loading={actionLoading}
      />

      <ConfirmModal
        show={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDeleteSubscriptionId(null);
        }}
        onConfirm={confirmDeleteSubscription}
        title="Aboneliği Sil"
        message={`${deleteSubscriptionId?.customer?.name || 'Müşteri'} müşterisinin aboneliğini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`}
        confirmText="Sil"
        cancelText="İptal"
        variant="danger"
        loading={actionLoading}
      />

      <ConfirmModal
        show={showCancelSuccess}
        onClose={() => {
          setShowCancelSuccess(false);
          setCancelResponse(null);
          refresh();
        }}
        onConfirm={() => {
          setShowCancelSuccess(false);
          setCancelResponse(null);
          refresh();
        }}
        title="Abonelik İptal Edildi"
        message={
          <div>
            <div className="alert alert-success mb-3">
              <i className="fas fa-check-circle me-2"></i>
              Abonelik başarıyla iptal edildi.
            </div>
            {cancelResponse && (
              <div>
                <h6>İptal Detayları:</h6>
                <ul className="list-unstyled mb-0 card card-body bg-light">
                  <li className="mb-2"><strong>İyzico Durumu:</strong> {cancelResponse.iyzicoSuccess ? 'Başarılı' : (cancelResponse.iyzicoError || 'Bilinmiyor')}</li>
                  <li className="mb-2"><strong>Müşteri Talebi:</strong> {cancelResponse.cancelOrders || 'Müşteri iade talebinde bulunmadı.'}</li>
                  {cancelResponse.note && (
                    <li className="text-warning"><small><i className="fas fa-info-circle me-1"></i>{cancelResponse.note}</small></li>
                  )}
                </ul>
              </div>
            )}
          </div>
        }
        confirmText="Tamam"
        cancelText=""
        variant="success"
        size="md"
      />
    </div>
  );
}

export default SubscriptionManagement; 